import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Tooltip,
  Chip,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { format } from 'date-fns';

interface PlmCurtailmentTask {
  _id: string;
  scheduledStart: number;
  scheduledEnd: number;
  status: string;
  batchId: number;
  estimatedReductionKW?: number;
}

interface PlmCurtailmentTask {
  _id: string;
  scheduledStart: number;
  scheduledEnd: number;
  status: string;
  batchId: number;
  estimatedReductionKW?: number;
  plmEventResponseId: string;
}

interface PlmScheduleTimelineDialogProps {
  open: boolean;
  onClose: () => void;
  deviceName: string;
  tasks: PlmCurtailmentTask[];
  eventResponseStart?: number;
  eventResponseEnd?: number;
}

export function PlmScheduleTimelineDialog({
  open,
  onClose,
  deviceName,
  tasks,
  eventResponseStart,
  eventResponseEnd,
}: PlmScheduleTimelineDialogProps) {
  const [selectedTask, setSelectedTask] = useState<PlmCurtailmentTask | null>(
    null,
  );

  // CSS keyframes for the pulsing gradient animation
  const keyframesStyle = `
  @keyframes rotateGradient {
    0% { background-position: 200% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

  if (!tasks || tasks.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <TimelineIcon color="info" />
            <Typography variant="h6">PLM Schedule Timeline</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            No PLM curtailment tasks found for device{' '}
            <strong>{deviceName}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Sort tasks by start time
  const sortedTasks = [...tasks].sort(
    (a, b) => a.scheduledStart - b.scheduledStart,
  );

  // Find the overall time range - use event response times if available, otherwise fall back to task times
  const earliestStart =
    eventResponseStart || Math.min(...tasks.map((t) => t.scheduledStart));
  const latestEnd =
    eventResponseEnd || Math.max(...tasks.map((t) => t.scheduledEnd));
  const totalDuration = latestEnd - earliestStart;

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return '#2196f3'; // Blue - represents future/pending
      case 'active':
        return '#4caf50'; // Green - represents running/working (will be overridden with animation)
      case 'completed':
        return '#8bc34a'; // Light green - represents success
      case 'verifying':
        return '#ff9800'; // Orange - represents checking/verification
      case 'transitioning':
        return '#9c27b0'; // Purple - represents state change
      case 'aborted':
        return '#ff5722'; // Deep orange - represents stopped/cancelled
      case 'missed':
        return '#795548'; // Brown - represents missed/expired
      default:
        return '#757575'; // Default gray
    }
  };

  // Helper function to get status styles including animations
  const getStatusStyles = (status: string) => {
    const baseColor = getStatusColor(status);

    if (status === 'active') {
      return {
        background: `linear-gradient(90deg,
          ${baseColor} 0%,
          #ffeb3b 15%,
          #4caf50 30%,
          #ffeb3b 45%,
          ${baseColor} 60%,
          #ffeb3b 75%,
          #4caf50 90%,
          ${baseColor} 100%)`,
        backgroundSize: '200% 100%',
        animation: 'rotateGradient 1.5s linear infinite',
      };
    }

    return {
      backgroundColor: baseColor,
    };
  };

  // Helper function to get display status (for grouping similar statuses)
  const getDisplayStatus = (status: string) => {
    if (
      ['pending', 'stopping_aborted', 'stopping_completed'].includes(status)
    ) {
      return 'verifying';
    }
    if (['execute_retry', 'abort_retry', 'complete_retry'].includes(status)) {
      return 'transitioning';
    }
    return status;
  };

  // Helper function to get detailed status for hover tooltips
  const getDetailedStatus = (status: string) => {
    if (
      ['pending', 'stopping_aborted', 'stopping_completed'].includes(status)
    ) {
      let targetState = 'unknown';
      if (status === 'stopping_completed') targetState = 'completed';
      else if (status === 'stopping_aborted') targetState = 'aborted';
      else if (status === 'pending') targetState = 'active';
      return `verifying (${targetState})`;
    }
    if (['execute_retry', 'abort_retry', 'complete_retry'].includes(status)) {
      let targetState = 'unknown';
      if (status === 'complete_retry') targetState = 'completed';
      else if (status === 'abort_retry') targetState = 'aborted';
      else if (status === 'execute_retry') targetState = 'active';
      return `transitioning (${targetState})`;
    }
    return status;
  };

  // Helper function to format duration
  const formatDuration = (start: number, end: number) => {
    const duration = end - start;
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  };

  // Helper function to calculate position and width for timeline bars
  const getTimelineBarProps = (task: PlmCurtailmentTask) => {
    const left = ((task.scheduledStart - earliestStart) / totalDuration) * 100;
    const width =
      ((task.scheduledEnd - task.scheduledStart) / totalDuration) * 100;
    return { left: `${left}%`, width: `${width}%` };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Inject CSS keyframes for animations */}
      <style>{keyframesStyle}</style>

      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <TimelineIcon color="info" />
          <Typography variant="h6">
            PLM Schedule Timeline - {deviceName}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Device Info Header */}
        <Box
          mb={3}
          sx={{
            textAlign: 'center',
            p: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {deviceName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Timeline shows {tasks.length} PLM curtailment tasks from{' '}
            {format(earliestStart, 'MMM dd, yyyy HH:mm')} to{' '}
            {format(latestEnd, 'MMM dd, yyyy HH:mm')}
            {eventResponseStart && eventResponseEnd && (
              <>
                {' '}
                (Full event duration:{' '}
                {Math.round(
                  (eventResponseEnd - eventResponseStart) / 60000,
                )}{' '}
                minutes)
              </>
            )}
          </Typography>
        </Box>

        {/* Timeline Container */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            position: 'relative',
            minHeight: '200px',
            backgroundColor: '#fafafa',
          }}
        >
          {/* Time markers */}
          <Box sx={{ position: 'relative', mb: 3 }}>
            {Array.from({ length: 6 }, (_, i) => {
              const time = earliestStart + (totalDuration * i) / 5;
              const left = (i / 5) * 100;
              return (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    left: `${left}%`,
                    transform: 'translateX(-50%)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {format(time, 'HH:mm')}
                  </Typography>
                  <Box
                    sx={{
                      width: '1px',
                      height: '8px',
                      backgroundColor: '#ccc',
                      mx: 'auto',
                      mt: 0.5,
                    }}
                  />
                </Box>
              );
            })}
          </Box>

          {/* Timeline bars */}
          <Box
            sx={{
              position: 'relative',
              height: '60px',
              backgroundImage: `
                linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px),
                linear-gradient(0deg, rgba(0,0,0,0.05) 1px, transparent 1px)
              `,
              backgroundSize: '100px 40px, 100% 20px',
            }}
          >
            {sortedTasks.map((task) => {
              const { left, width } = getTimelineBarProps(task);
              const isSelected = selectedTask?._id === task._id;

              return (
                <Tooltip
                  key={task._id}
                  title={
                    <Box>
                      <Typography variant="body2">
                        <strong>Batch {task.batchId}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Start:{' '}
                        {format(task.scheduledStart, 'MMM dd, yyyy HH:mm')}
                      </Typography>
                      <Typography variant="body2">
                        End: {format(task.scheduledEnd, 'MMM dd, yyyy HH:mm')}
                      </Typography>
                      <Typography variant="body2">
                        Duration:{' '}
                        {formatDuration(task.scheduledStart, task.scheduledEnd)}
                      </Typography>
                      <Typography variant="body2">
                        Status: {getDetailedStatus(task.status)}
                      </Typography>
                      {task.estimatedReductionKW && (
                        <Typography variant="body2">
                          Est. Reduction: {task.estimatedReductionKW} kW
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                >
                  <Box
                    onClick={() => setSelectedTask(task)}
                    sx={{
                      position: 'absolute',
                      left,
                      width,
                      height: '40px',
                      ...getStatusStyles(task.status),
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: isSelected
                        ? '3px solid #000'
                        : '1px solid rgba(0,0,0,0.2)',
                      top: '10px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                      },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>

          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ mr: 1, fontWeight: 'bold' }}>
              Status Legend:
            </Typography>
            {[
              'scheduled',
              'active',
              'completed',
              'verifying',
              'transitioning',
              'aborted',
              'missed',
            ].map((status) => (
              <Chip
                key={status}
                label={status}
                size="small"
                sx={{
                  ...getStatusStyles(
                    status === 'verifying'
                      ? 'pending'
                      : status === 'transitioning'
                        ? 'execute_retry'
                        : status,
                  ),
                  color: 'white',
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Selected Task Details */}
        {selectedTask && (
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Task Details
            </Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Task ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {selectedTask._id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Batch ID
                </Typography>
                <Typography variant="body1">{selectedTask.batchId}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Start Time
                </Typography>
                <Typography variant="body1">
                  {format(selectedTask.scheduledStart, 'MMM dd, yyyy HH:mm:ss')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  End Time
                </Typography>
                <Typography variant="body1">
                  {format(selectedTask.scheduledEnd, 'MMM dd, yyyy HH:mm:ss')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body1">
                  {formatDuration(
                    selectedTask.scheduledStart,
                    selectedTask.scheduledEnd,
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={getDisplayStatus(selectedTask.status)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(selectedTask.status),
                    color: 'white',
                  }}
                />
              </Box>
            </Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
